import { useMemo } from "react";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { boundedLimit, linearPercent } from "../../lib/decisionScales.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { KernelEmpty } from "./shared.jsx";

export const kind = "refBars";

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const maxItems = boundedLimit(spec.maxItems, 12, 24);
    const rows = useMemo(() => (Array.isArray(data) ? data : [])
        .map((row) => ({
            label: String(row?.label ?? "Unlabelled"),
            value: asNumber(row?.value),
            detail: row?.detail == null ? null : String(row.detail),
        }))
        .filter((row) => row.value != null && row.value >= 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, maxItems), [data, maxItems]);
    if (!rows.length) return <KernelEmpty message={spec.empty || "No values to compare"} />;

    const reference = asNumber(spec.reference?.value);
    const hasReference = reference != null && reference >= 0;
    const max = Math.max(hasReference ? reference : 0, ...rows.map((row) => row.value)) || 1;
    const referencePercent = linearPercent(reference, max);
    const format = spec.format || "number";
    const focused = spec.focusValue == null ? null : String(spec.focusValue);

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {hasReference ? (
                <div className="mb-[7px] text-[10.5px] text-[color:var(--muted)]">
                    {spec.reference.label || "Reference"}: <span className="font-mono text-[color:var(--ink)]">{formatValue(reference, format)}</span>
                </div>
            ) : null}
            {rows.map((row, index) => {
                const above = hasReference && row.value > reference;
                const selected = focused != null && focused === row.label;
                const clickable = Boolean(onFocus);
                return (
                    <div
                        key={`${row.label}-${index}`}
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-pressed={clickable ? selected : undefined}
                        onClick={clickable ? () => onFocus(spec.groupField || "label", selected ? null : row.label) : undefined}
                        onKeyDown={clickable ? (event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            onFocus(spec.groupField || "label", selected ? null : row.label);
                        } : undefined}
                        className={`grid grid-cols-[minmax(90px,24%)_minmax(0,1fr)_auto] items-center gap-[8px] text-[11px]${clickable ? " cursor-pointer" : ""}`}
                        style={{ minHeight: "19px", opacity: focused != null && !selected ? 0.35 : 1 }}
                    >
                        <OverflowTooltip as="div" text={row.label} className="min-w-0 text-left font-mono text-[color:var(--ink)]">{row.label}</OverflowTooltip>
                        <div className="relative h-[15px]">
                            <div className="absolute top-[3px] left-0 h-[9px] bg-[color:var(--muted)]" style={{ width: `${linearPercent(row.value, max)}%`, opacity: above ? 0.55 : 0.8 }} />
                            {hasReference ? <div className="absolute inset-y-0 w-px bg-[color:var(--ink)] opacity-60" style={{ left: `${referencePercent}%` }} /> : null}
                        </div>
                        <span className="font-mono text-[10.5px] tabular-nums text-[color:var(--ink)]" title={row.detail || undefined}>{formatValue(row.value, format)}</span>
                    </div>
                );
            })}
            {hasReference ? <div className="mt-[5px] text-[9.5px] text-[color:var(--muted-soft)]">The vertical rule is the shared reference.</div> : null}
        </div>
    );
}
